import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Search, Loader, AlertCircle, Gavel, SlidersHorizontal, X } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { bureauChipLabel } from '../components/proceedings/proceedingsBureauUtils';
import { ProceedingListCard } from '../components/proceedings/ProceedingListCard';
import { ProceedingsPagination } from '../components/proceedings/ProceedingsPagination';

interface Proceeding {
  docket_number: string;
  proceeding_name: string;
  status: string;
  processing_status: string;
  updated_at: string;
  created_at: string;
  last_processed_at?: string | null;
  bureau?: string | null;
  has_nrpm?: boolean;
  last_document_count?: number | null;
}

interface PaginationInfo {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPrevPage: boolean;
}

const DEFAULT_PAGE_LIMIT = 10;
const PAGE_SIZE_OPTIONS = [10, 25, 50, 100] as const;

function parsePageLimitParam(raw: string | null): number {
  const n = raw ? parseInt(raw, 10) : NaN;
  if (Number.isFinite(n) && (PAGE_SIZE_OPTIONS as readonly number[]).includes(n)) return n;
  return DEFAULT_PAGE_LIMIT;
}

type StatusFilter = 'all' | 'active' | 'pending';
type SortMode = 'recent' | 'volume' | 'alpha' | 'docket';

function sortParams(mode: SortMode): { sortBy: string; sortOrder: 'asc' | 'desc' } {
  switch (mode) {
    case 'volume':
      return { sortBy: 'last_document_count', sortOrder: 'desc' };
    case 'alpha':
      return { sortBy: 'proceeding_name', sortOrder: 'asc' };
    case 'docket':
      return { sortBy: 'docket_number', sortOrder: 'asc' };
    case 'recent':
    default:
      return { sortBy: 'last_processed_at', sortOrder: 'desc' };
  }
}

export const ProceedingsPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const qFromUrl = searchParams.get('q') ?? '';
  const limitFromUrl = searchParams.get('limit');

  const [searchQuery, setSearchQuery] = useState(() => qFromUrl);
  const [proceedings, setProceedings] = useState<Proceeding[]>([]);
  const [nprmStatusByDocket, setNprmStatusByDocket] = useState<Record<string, 'unknown' | 'has' | 'none'>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [nprmUpdateMessage, setNprmUpdateMessage] = useState<string | null>(null);
  const [pagination, setPagination] = useState<PaginationInfo>({
    page: 1,
    limit: DEFAULT_PAGE_LIMIT,
    total: 0,
    totalPages: 0,
    hasNextPage: false,
    hasPrevPage: false,
  });
  const [pageLimit, setPageLimit] = useState(() => parsePageLimitParam(searchParams.get('limit')));
  const [currentPage, setCurrentPage] = useState(1);
  const [debouncedSearch, setDebouncedSearch] = useState(() => qFromUrl);
  const [selectedBureau, setSelectedBureau] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [sortMode, setSortMode] = useState<SortMode>('recent');
  const [hasNrpmOnly, setHasNrpmOnly] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [availableBureaus, setAvailableBureaus] = useState<string[]>([]);
  const skipNextSearchParamsSync = useRef(false);

  useEffect(() => {
    if (skipNextSearchParamsSync.current) {
      skipNextSearchParamsSync.current = false;
      return;
    }
    setSearchQuery(qFromUrl);
    setDebouncedSearch(qFromUrl);
    setPageLimit(parsePageLimitParam(limitFromUrl));
  }, [qFromUrl, limitFromUrl]);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchQuery), 500);
    return () => clearTimeout(t);
  }, [searchQuery]);

  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch, selectedBureau, statusFilter, sortMode, hasNrpmOnly, pageLimit]);

  useEffect(() => {
    const q = debouncedSearch.trim();
    const next = new URLSearchParams(window.location.search);
    let changed = false;
    const curQ = next.get('q') ?? '';
    if (curQ !== q) {
      if (q) next.set('q', q);
      else next.delete('q');
      changed = true;
    }
    const wantLimitStr = pageLimit === DEFAULT_PAGE_LIMIT ? null : String(pageLimit);
    const curLimit = next.get('limit');
    if (wantLimitStr !== curLimit) {
      if (pageLimit === DEFAULT_PAGE_LIMIT) next.delete('limit');
      else next.set('limit', String(pageLimit));
      changed = true;
    }
    if (!changed) return;
    skipNextSearchParamsSync.current = true;
    setSearchParams(next, { replace: true });
  }, [debouncedSearch, pageLimit, setSearchParams]);

  const processingStatusParam =
    statusFilter === 'pending' ? 'pending' : statusFilter === 'active' ? 'active' : '';

  const fetchProceedings = useCallback(
    async (
      page: number,
      search: string,
      bureau: string,
      hasNrpm: boolean,
      status: string,
      sort: SortMode,
      limit: number
    ) => {
      try {
        setLoading(true);
        setError(null);
        const { sortBy, sortOrder } = sortParams(sort);
        const apiUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/get-open-proceedings`;
        const params = new URLSearchParams({
          page: page.toString(),
          limit: limit.toString(),
          sortBy,
          sortOrder,
          ...(search.trim() && { search: search.trim() }),
          ...(bureau.trim() && { bureau: bureau.trim() }),
          ...(hasNrpm && { hasNrpm: 'true' }),
          ...(status && { processingStatus: status }),
        });

        const response = await fetch(`${apiUrl}?${params}`, {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
            'Content-Type': 'application/json',
          },
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
        }

        const data = await response.json();
        setProceedings(data.data || []);
        setNprmStatusByDocket((prev) => {
          const next = { ...prev };
          for (const p of (data.data || []) as Proceeding[]) {
            if (p?.docket_number && next[p.docket_number] === undefined) {
              next[p.docket_number] = 'unknown';
            }
          }
          return next;
        });
        if (data.pagination) setPagination(data.pagination);
      } catch (err) {
        console.error('Error fetching proceedings:', err);
        setError(err instanceof Error ? err.message : 'Failed to fetch proceedings');
        setProceedings([]);
      } finally {
        setLoading(false);
      }
    },
    []
  );

  useEffect(() => {
    fetchProceedings(
      currentPage,
      debouncedSearch,
      selectedBureau,
      hasNrpmOnly,
      processingStatusParam,
      sortMode,
      pageLimit
    );
  }, [
    currentPage,
    debouncedSearch,
    selectedBureau,
    hasNrpmOnly,
    processingStatusParam,
    sortMode,
    pageLimit,
    fetchProceedings,
  ]);


  const normalizeBureauName = (bureau: string | null | undefined): string | null => {
    if (!bureau) return null;
    let normalized = String(bureau).trim();
    if (!normalized) return null;
    normalized = normalized.replace(/\s*&\s*/g, ' and ');
    normalized = normalized.replace(/\s+Bureau\s*$/i, '');
    normalized = normalized.replace(/\s+/g, ' ').trim();
    return normalized || null;
  };

  useEffect(() => {
    const fetchAllBureaus = async () => {
      try {
        const { data, error: supaErr } = await supabase
          .from('open_proceedings')
          .select('bureau')
          .eq('status', 'open')
          .not('bureau', 'is', null);

        if (supaErr) {
          const bureaus = Array.from(
            new Set(
              proceedings
                .map((p) => normalizeBureauName(p.bureau))
                .filter((b): b is string => !!b)
                .sort()
            )
          );
          setAvailableBureaus(bureaus);
          return;
        }
        const uniqueBureaus = Array.from(
          new Set(
            (data || [])
              .map((item: { bureau: string }) => normalizeBureauName(item.bureau))
              .filter((b): b is string => !!b)
              .sort()
          )
        );
        setAvailableBureaus(uniqueBureaus);
      } catch {
        const bureaus = Array.from(
          new Set(
            proceedings
              .map((p) => normalizeBureauName(p.bureau))
              .filter((b): b is string => !!b)
              .sort()
          )
        );
        setAvailableBureaus(bureaus);
      }
    };
    fetchAllBureaus();
  }, [proceedings.length]);

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      setCurrentPage(newPage);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const formatShortDate = (dateString: string) => {
    if (!dateString) return '—';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return '—';
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    } catch {
      return '—';
    }
  };

  const handleDocketClick = (docketNumber: string) => {
    navigate(`/proceedings/${encodeURIComponent(docketNumber)}`);
  };


  const clearAllFilters = () => {
    setSearchQuery('');
    setDebouncedSearch('');
    setSelectedBureau('');
    setStatusFilter('all');
    setSortMode('recent');
    setHasNrpmOnly(false);
    setCurrentPage(1);
    const next = new URLSearchParams(window.location.search);
    next.delete('q');
    setSearchParams(next, { replace: true });
    setFiltersOpen(false);
  };

  const filterSummaryParts: string[] = [];
  if (selectedBureau) filterSummaryParts.push(bureauChipLabel(selectedBureau));
  if (statusFilter === 'pending') filterSummaryParts.push('pending');
  if (statusFilter === 'active') filterSummaryParts.push('active');
  if (hasNrpmOnly) filterSummaryParts.push('has NPRM');
  if (debouncedSearch.trim()) filterSummaryParts.push(`“${debouncedSearch.trim()}”`);
  const filterSummary =
    filterSummaryParts.length > 0 ? ` · filtered by ${filterSummaryParts.join(', ')}` : '';

  const hasActiveFilters =
    Boolean(debouncedSearch.trim()) ||
    Boolean(selectedBureau) ||
    statusFilter !== 'all' ||
    sortMode !== 'recent' ||
    hasNrpmOnly;

  // Layout only — no background/text color here, so the active/inactive color
  // set below applies cleanly. (Layering bg/text on top of a base set lets
  // Tailwind's CSS ordering pick the loser, e.g. white text on a white chip.)
  const chipBase = 'rounded-full border px-2.5 py-1 text-xs font-normal transition-colors';
  const chipInactive = 'border-cg-border bg-white text-neutral-600 hover:border-neutral-400 hover:text-cg-ink';
  const chipGreenActive = 'border-[#a7d9bf] bg-[#eaf6f0] text-[#1a7a4b] hover:bg-[#d4eee3]';

  const sidebarInner = (
    <>
      <div>
        <div className="mb-2.5 text-[10px] font-medium uppercase tracking-[0.09em] text-neutral-400">Search</div>
        <div className="flex items-center gap-2 rounded-[7px] border border-cg-border bg-cg-cream px-2.5 py-2">
          <Search className="h-3.5 w-3.5 shrink-0 text-neutral-400" aria-hidden />
          <input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Docket, keyword, topic…"
            className="w-full border-0 bg-transparent font-sans text-[13px] text-cg-ink outline-none placeholder:text-neutral-400"
            aria-label="Search proceedings"
          />
        </div>
      </div>

      <div className="h-px bg-[#eeeee9]" />


      <div>
        <div className="mb-2.5 text-[10px] font-medium uppercase tracking-[0.09em] text-neutral-400">Bureau</div>
        <div className="flex flex-wrap gap-1.5">
          {/* Several full bureau names collapse to the same short chip label
              (e.g. anything containing "media" -> "MB"), so dedupe by the
              rendered label — otherwise the sidebar shows two identical "MB"
              chips. Keep the first full name as the representative to filter by. */}
          {Array.from(
            availableBureaus
              .reduce((map, bureau) => {
                const short = bureauChipLabel(bureau);
                if (!map.has(short)) map.set(short, bureau);
                return map;
              }, new Map<string, string>())
              .entries()
          ).map(([short, bureau]) => {
            const isSel = selectedBureau === bureau;
            return (
              <button
                key={short}
                type="button"
                title={bureau}
                onClick={() => setSelectedBureau(isSel ? '' : bureau)}
                className={`${chipBase} ${isSel ? chipGreenActive : chipInactive}`}
              >
                {short}
              </button>
            );
          })}
        </div>
      </div>

      <div className="h-px bg-[#eeeee9]" />

      <div>
        <div className="mb-2.5 text-[10px] font-medium uppercase tracking-[0.09em] text-neutral-400">Sort by</div>
        <select
          value={sortMode}
          onChange={(e) => setSortMode(e.target.value as SortMode)}
          aria-label="Sort proceedings by"
          className="w-full cursor-pointer rounded-[7px] border border-cg-border bg-white py-1.5 pl-2.5 pr-2 font-sans text-[13px] text-cg-ink outline-none"
        >
          <option value="recent">Most recent filings</option>
          <option value="volume">Filing volume</option>
          <option value="alpha">Alphabetical</option>
          <option value="docket">Docket number</option>
        </select>
      </div>

      <div className="h-px bg-[#eeeee9]" />

      <div className="flex flex-col gap-2.5">
        <div className="text-[10px] font-medium uppercase tracking-[0.09em] text-neutral-400">Display</div>
        <div className="flex items-center justify-between gap-3">
          <span className="text-[13px] font-light text-neutral-600">Has NPRM</span>
          <button
            type="button"
            role="switch"
            aria-checked={hasNrpmOnly}
            aria-label="Has NPRM"
            onClick={() => setHasNrpmOnly((v) => !v)}
            className={`relative h-[19px] w-[34px] shrink-0 rounded-full transition-colors ${
              hasNrpmOnly ? 'bg-cg-green' : 'bg-neutral-300'
            }`}
          >
            <span
              className={`absolute top-0.5 h-[15px] w-[15px] rounded-full bg-white shadow transition-[right] ${
                hasNrpmOnly ? 'right-0.5' : 'right-[17px]'
              }`}
            />
          </button>
        </div>
      </div>

    </>
  );

  return (
    <div className="min-h-[calc(100vh-52px)] w-full bg-cg-cream font-sans text-cg-ink">
      <div className="flex min-h-[calc(100vh-52px)]">
        {/* Desktop sidebar */}
        <aside className="hidden w-[248px] shrink-0 flex-col gap-7 border-r border-cg-border bg-white px-5 py-7 sm:flex">
          {sidebarInner}
        </aside>

        {/* Mobile filter drawer */}
        {filtersOpen && (
          <div className="fixed inset-0 z-40 sm:hidden" aria-modal>
            <button
              type="button"
              className="absolute inset-0 bg-black/30"
              aria-label="Close filters"
              onClick={() => setFiltersOpen(false)}
            />
            <div className="absolute bottom-0 left-0 right-0 max-h-[85vh] overflow-y-auto rounded-t-2xl bg-white p-5 shadow-xl">
              <div className="mb-4 flex items-center justify-between">
                <span className="font-medium text-cg-ink">Filters</span>
                <button
                  type="button"
                  onClick={() => setFiltersOpen(false)}
                  className="rounded-md p-1 text-neutral-500 hover:bg-neutral-100"
                  aria-label="Close"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              <div className="flex flex-col gap-6">{sidebarInner}</div>
            </div>
          </div>
        )}

        <div className="min-w-0 flex-1 px-5 py-7 sm:px-8 sm:pb-16">
          <div className="mb-6 flex flex-wrap items-start justify-between gap-3">
            <div>
              <h1 className="font-display text-[1.75rem] font-normal leading-tight tracking-tight text-cg-ink sm:text-[2rem]">
                Open FCC Proceedings
              </h1>
              <p className="mt-1 text-[13px] font-light text-neutral-400">
                {!loading && pagination.total > 0
                  ? `${pagination.total} results${filterSummary}`
                  : loading
                    ? 'Loading…'
                    : ''}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                className="flex h-9 items-center gap-2 rounded-md border border-cg-border bg-white px-3 text-[13px] text-cg-ink sm:hidden"
                onClick={() => setFiltersOpen(true)}
              >
                <SlidersHorizontal className="h-4 w-4" />
                Filters
              </button>
            </div>
          </div>

          {hasActiveFilters && (
            <div className="mb-5 flex flex-wrap items-center gap-2">
              <span className="text-xs font-light text-neutral-400">Filters:</span>
              {debouncedSearch.trim() && (
                <button
                  type="button"
                  onClick={() => {
                    setSearchQuery('');
                    setDebouncedSearch('');
                    const next = new URLSearchParams(window.location.search);
                    next.delete('q');
                    setSearchParams(next, { replace: true });
                  }}
                  className="inline-flex items-center gap-1 rounded-full border border-[#a7d9bf] bg-[#eaf6f0] px-2.5 py-0.5 text-xs text-[#1a7a4b] hover:bg-[#d4eee3]"
                >
                  “{debouncedSearch.trim()}” <span className="text-sm leading-none">×</span>
                </button>
              )}
              {selectedBureau && (
                <button
                  type="button"
                  onClick={() => setSelectedBureau('')}
                  className="inline-flex items-center gap-1 rounded-full border border-[#a7d9bf] bg-[#eaf6f0] px-2.5 py-0.5 text-xs text-[#1a7a4b] hover:bg-[#d4eee3]"
                >
                  {bureauChipLabel(selectedBureau)} <span className="text-sm leading-none">×</span>
                </button>
              )}
              {statusFilter !== 'all' && (
                <button
                  type="button"
                  onClick={() => setStatusFilter('all')}
                  className="inline-flex items-center gap-1 rounded-full border border-[#a7d9bf] bg-[#eaf6f0] px-2.5 py-0.5 text-xs text-[#1a7a4b] hover:bg-[#d4eee3]"
                >
                  {statusFilter === 'active' ? 'Active' : 'Pending'} <span className="text-sm leading-none">×</span>
                </button>
              )}
              {hasNrpmOnly && (
                <button
                  type="button"
                  onClick={() => setHasNrpmOnly(false)}
                  className="inline-flex items-center gap-1 rounded-full border border-[#a7d9bf] bg-[#eaf6f0] px-2.5 py-0.5 text-xs text-[#1a7a4b] hover:bg-[#d4eee3]"
                >
                  Has NPRM <span className="text-sm leading-none">×</span>
                </button>
              )}
              {sortMode !== 'recent' && (
                <button
                  type="button"
                  onClick={() => setSortMode('recent')}
                  className="inline-flex items-center gap-1 rounded-full border border-[#a7d9bf] bg-[#eaf6f0] px-2.5 py-0.5 text-xs text-[#1a7a4b] hover:bg-[#d4eee3]"
                >
                  Sort:{' '}
                  {sortMode === 'volume'
                    ? 'Volume'
                    : sortMode === 'alpha'
                      ? 'A–Z'
                      : 'Docket'}{' '}
                  <span className="text-sm leading-none">×</span>
                </button>
              )}
              <button
                type="button"
                onClick={clearAllFilters}
                className="text-xs font-light text-neutral-400 underline underline-offset-2 hover:text-neutral-600"
              >
                Clear all
              </button>
            </div>
          )}

          {error && (
            <div className="mb-6 rounded-xl border border-red-200 bg-red-50 p-4">
              <div className="flex items-start gap-3">
                <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-red-600" />
                <div>
                  <h3 className="mb-1 text-sm font-semibold text-red-800">Error</h3>
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              </div>
            </div>
          )}

          {nprmUpdateMessage && !error && (
            <div className="mb-6 rounded-xl border border-violet-200 bg-violet-50 p-4">
              <p className="text-sm text-violet-900">{nprmUpdateMessage}</p>
            </div>
          )}

          {loading && (
            <div className="flex items-center justify-center py-16">
              <Loader className="h-8 w-8 animate-spin text-cg-green" />
              <span className="ml-3 text-sm font-light text-neutral-500">Loading proceedings…</span>
            </div>
          )}

          {!loading && proceedings.length > 0 && (
            <div>
              {proceedings.map((proceeding) => {
                let nprm = nprmStatusByDocket[proceeding.docket_number] ?? 'unknown';
                if (proceeding.has_nrpm) nprm = 'has';
                const bureauN = normalizeBureauName(proceeding.bureau);
                return (
                  <ProceedingListCard
                    key={proceeding.docket_number}
                    proceeding={proceeding}
                    nprmUi={nprm}
                    bureauNormalized={bureauN}
                    formatShortDate={formatShortDate}
                    onCardClick={() => handleDocketClick(proceeding.docket_number)}
                    onSummarizeClick={() => handleDocketClick(proceeding.docket_number)}
                  />
                );
              })}
            </div>
          )}

          {!loading && proceedings.length === 0 && !error && (
            <div className="py-16 text-center">
              <Gavel className="mx-auto mb-4 h-14 w-14 text-neutral-300" />
              <p className="text-base font-light text-neutral-600">
                {debouncedSearch || selectedBureau || hasNrpmOnly || statusFilter !== 'all'
                  ? 'No proceedings found matching your criteria.'
                  : 'No open proceedings found.'}
              </p>
            </div>
          )}

          <ProceedingsPagination
            currentPage={currentPage}
            totalPages={pagination.totalPages}
            hasPrevPage={pagination.hasPrevPage}
            hasNextPage={pagination.hasNextPage}
            onPageChange={handlePageChange}
            pageSize={pageLimit}
            pageSizeOptions={PAGE_SIZE_OPTIONS}
            onPageSizeChange={setPageLimit}
          />
        </div>
      </div>
    </div>
  );
};
